/**
 * Contentful GraphQL Client
 * Handles GraphQL queries to Contentful API
 */

export interface ContentfulGraphQLResponse<T> {
  data: T;
  errors?: Array<{
    message: string;
    locations?: Array<{ line: number; column: number }>;
    path?: Array<string | number>;
  }>;
}

export interface ContentfulGraphQLRequest {
  query: string;
  variables?: Record<string, unknown>;
}

/**
 * Get Contentful GraphQL endpoint URL
 */
const getContentfulGraphQLUrl = (): string => {
  const spaceId = process.env.NX_CONTENTFUL_SPACE_ID;
  const environment = process.env.NX_CONTENTFUL_ENVIRONMENT || 'master';

  if (!spaceId) {
    throw new Error('NX_CONTENTFUL_SPACE_ID is not defined');
  }

  return `https://graphql.contentful.com/content/v1/spaces/${spaceId}/environments/${environment}`;
};

/**
 * Execute a GraphQL query against Contentful
 */
export const contentfulGraphQLRequest = async <T>(
  query: string,
  variables?: Record<string, unknown>,
): Promise<ContentfulGraphQLResponse<T>> => {
  const url = getContentfulGraphQLUrl();
  const accessToken = process.env.NX_CONTENTFUL_ACCESS_TOKEN;

  if (!accessToken) {
    throw new Error('NX_CONTENTFUL_ACCESS_TOKEN is not defined');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (!response.ok) {
    throw new Error(`Contentful API error: ${response.status} ${response.statusText}`);
  }

  const result: ContentfulGraphQLResponse<T> = await response.json();

  if (result.errors && result.errors.length > 0) {
    const errorMessage = result.errors.map((e) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  return result;
};
